import { provideHttpClient } from '@angular/common/http';
import { ApplicationConfig, ErrorHandler, isDevMode, provideZoneChangeDetection, provideAppInitializer, inject } from '@angular/core';
import { LOCAL_STORAGE_KEY_ACTIVE_FILTER_NAME, LOCAL_STORAGE_KEY_FILTERS, LOCAL_STORAGE_KEY_USERNAME } from './const';
import { EconomyService } from './services/economy.service';
import { ToastService } from './services/toast.service';
import * as Sentry from "@sentry/angular";

const sentryErrorHandler = Sentry.createErrorHandler();

function sanitizeLabel(category: string, action: string, name?: string): string | undefined {
  if (!name) return undefined;

  if (category === 'bootstrap_error') {
    return undefined;
  }

  if (category === 'filter_migration' && action === 'error') {
    const versionPair = name.split(':')[0].trim();
    return versionPair.length <= 100 ? versionPair : versionPair.substring(0, 100);
  }

  if (action.includes('error') || action.includes('failed')) {
    return undefined;
  }

  if (category === 'login' && action !== 'started') {
    return undefined;
  }

  return name.length <= 100 ? name : name.substring(0, 100);
}

export function logEvent(category: string, action: string, name?: string) {
  const message = [category, action, name].filter(Boolean).join(' ');
  if (isDevMode()) {
    console.log(message);
    return;
  }

  if (category === 'trace') {
    Sentry.logger.trace(message, { category, action, name });
    return;
  }

  const metricName = `${category}.${action}`;
  const label = sanitizeLabel(category, action, name);
  Sentry.metrics.count(metricName, 1, {
    attributes: label ? { label } : {},
  });
}

export function logErrorWithDetails(msg: string, details?: Record<string, any>) {
  if (!isDevMode()) {
    Sentry.logger.error(msg, details);
  }
}

export function logError(msg: string, details?: string) {
  if (!isDevMode()) {
    Sentry.logger.error(msg, { details: details });
  }
}

export function trackError(err: Error) {
  console.error(err);
  if (!isDevMode()) {
    sentryErrorHandler.handleError(err);
  }
}

class LogErrorHandler implements ErrorHandler {
  private toastService = inject(ToastService);

  handleError(err: Error) {
    console.error(err);
    let msg = err.message || err.toString().substring(0, 200);
    this.toastService.raise('error', msg, 'An unexpected error occurred', 'This error was logged and will be investigated.');
    const activeFilterName = localStorage.getItem(LOCAL_STORAGE_KEY_ACTIVE_FILTER_NAME);
    const storedFilters: any[] = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY_FILTERS) || '[]');
    const activeFilter = storedFilters.find(f => f.name === activeFilterName) ?? null;
    logErrorWithDetails(err.toString(), {
      username: localStorage.getItem(LOCAL_STORAGE_KEY_USERNAME),
      activeFilter,
    });
    if (!isDevMode()) {
      sentryErrorHandler.handleError(err);
    }
  }
}

function initializeEconomyManager(): Promise<void> {
  const economyManager = inject(EconomyService);
  const sentryTraceService = inject(Sentry.TraceService);
  return economyManager.initialize();
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideHttpClient(),
    { provide: ErrorHandler, useClass: LogErrorHandler },
    provideAppInitializer(initializeEconomyManager),
  ]
};
